import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Trophy, Users, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const Tournoi = () => {
  const [formData, setFormData] = useState({
    nomTournoi: '',
    nombreJoueurs: '',
    participation: ''
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [commercials, setCommercials] = useState([]);
  const [selectedCommercial, setSelectedCommercial] = useState(null);

  useEffect(() => {
    fetchCommercials();
  }, []);

  const fetchCommercials = async () => {
    try {
      const response = await axios.get(`${API}/commercials`, { withCredentials: true });
      setCommercials(response.data);
    } catch (error) {
      console.error('Erreur commerciaux:', error);
    }
  };

  const calculatePreview = () => {
    const nombreJoueurs = parseInt(formData.nombreJoueurs) || 0;
    const participation = parseInt(formData.participation) || 0;
    const total = nombreJoueurs * participation;
    const gainVainqueur = Math.floor(total * 0.4);
    const gainDeuxieme = Math.floor(total * 0.2);
    const gainPlayCity = Math.floor(total * 0.4);

    return {
      total,
      gainVainqueur,
      gainDeuxieme,
      gainPlayCity
    };
  };

  const handleInputChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    // Update preview if numbers are valid
    if (field === 'nombreJoueurs' || field === 'participation') {
      if (newFormData.nombreJoueurs && newFormData.participation) {
        setPreview(calculatePreview());
      } else {
        setPreview(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nomTournoi || !formData.nombreJoueurs || !formData.participation) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    if (!selectedCommercial) {
      toast.error('Veuillez sélectionner l\'organisateur du tournoi');
      return;
    }

    setLoading(true);
    try {
      // Find commercial name
      const commercial = commercials.find(c => c.id === selectedCommercial);

      await axios.post(`${API}/tournaments`, {
        nomTournoi: formData.nomTournoi,
        nombreJoueurs: parseInt(formData.nombreJoueurs),
        participation: parseInt(formData.participation),
        commercialId: selectedCommercial,
        commercialName: commercial?.nom
      }, { withCredentials: true });

      toast.success('Tournoi enregistré avec succès !');
      
      // Reset form
      setFormData({
        nomTournoi: '',
        nombreJoueurs: '',
        participation: ''
      });
      setSelectedCommercial(null);
      setPreview(null);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const previewData = formData.nombreJoueurs && formData.participation ? calculatePreview() : null;

  return (
    <div className="space-y-6" data-testid="tournoi-page">
      <h1 className="text-3xl lg:text-4xl font-bold text-[#EAEAEA]">Gestion des Tournois</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <Card className="glass-effect border-[#C6A15B]/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <Trophy className="w-6 h-6 text-[#C6A15B]" />
              <h2 className="text-xl font-bold text-[#EAEAEA]">Nouveau Tournoi</h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-[#EAEAEA]">Nom du Tournoi *</Label>
                <Input
                  value={formData.nomTournoi}
                  onChange={(e) => handleInputChange('nomTournoi', e.target.value)}
                  placeholder="Ex: Tournoi FIFA 2024"
                  className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                />
              </div>

              <div>
                <Label className="text-[#EAEAEA]">Nombre de Joueurs *</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.nombreJoueurs}
                  onChange={(e) => handleInputChange('nombreJoueurs', e.target.value)}
                  placeholder="Ex: 16"
                  className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                />
              </div>

              <div>
                <Label className="text-[#EAEAEA]">Participation par Joueur (FCFA) *</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.participation}
                  onChange={(e) => handleInputChange('participation', e.target.value)}
                  placeholder="Ex: 5000"
                  className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                />
              </div>

              <div>
                <Label className="text-[#EAEAEA]">Organisé par *</Label>
                <Select value={selectedCommercial} onValueChange={setSelectedCommercial}>
                  <SelectTrigger className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]">
                    <SelectValue placeholder="Sélectionner le commercial..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
                    {commercials.map(commercial => (
                      <SelectItem key={commercial.id} value={commercial.id}>
                        {commercial.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B] font-bold py-6 text-lg"
              >
                {loading ? 'Enregistrement...' : 'Enregistrer le Tournoi'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="glass-effect border-[#C6A15B]/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <DollarSign className="w-6 h-6 text-[#C6A15B]" />
              <h2 className="text-xl font-bold text-[#EAEAEA]">Aperçu des Gains</h2>
            </div>

            {previewData ? (
              <div className="space-y-4">
                <div className="bg-[#0B132B] rounded-lg p-4">
                  <p className="text-[#EAEAEA]/60 text-sm mb-1">Total du Tournoi</p>
                  <p className="text-3xl font-bold text-[#C6A15B]">
                    {previewData.total.toLocaleString()} FCFA
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div className="bg-[#0B132B] rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy className="w-5 h-5 text-yellow-500" />
                      <p className="text-[#EAEAEA] font-semibold">Gain Vainqueur (40%)</p>
                    </div>
                    <p className="text-2xl font-bold text-[#EAEAEA]">
                      {previewData.gainVainqueur.toLocaleString()} FCFA
                    </p>
                  </div>

                  <div className="bg-[#0B132B] rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Trophy className="w-5 h-5 text-gray-400" />
                      <p className="text-[#EAEAEA] font-semibold">Gain 2ème (20%)</p>
                    </div>
                    <p className="text-2xl font-bold text-[#EAEAEA]">
                      {previewData.gainDeuxieme.toLocaleString()} FCFA
                    </p>
                  </div>

                  <div className="bg-[#C6A15B]/20 border-2 border-[#C6A15B] rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-[#C6A15B]" />
                      <p className="text-[#EAEAEA] font-semibold">Gain PlayCity (40%)</p>
                    </div>
                    <p className="text-2xl font-bold text-[#C6A15B]">
                      {previewData.gainPlayCity.toLocaleString()} FCFA
                    </p>
                    <p className="text-[#EAEAEA]/60 text-xs mt-1">
                      Ajouté au CA JEUX
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <p className="text-[#EAEAEA]/60">
                  Remplissez les champs pour voir l'aperçu
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Tournoi;
